import { useRef, useEffect, useCallback, useState } from 'react';
import axios from 'axios';

export function useProctoring({
  attemptId, maxWarnings = 3, enabled = false,
  onTerminate, onFullscreenExit, wsRef,
  requireFullscreen = true,
  captureSnapshot = null,
  disabledViolations = [],   // FIX 1: violation types that warn but don't count
  initialWarnings = 0,       // FIX 2: restore from server after refresh
}) {
  const videoRef    = useRef(null);
  const canvasRef   = useRef(null);
  const overlayRef  = useRef(null);
  const streamRef   = useRef(null);
  const intervalRef = useRef(null);

  const enabledRef      = useRef(false);
  const cameraReadyRef  = useRef(false);
  const requireFsRef    = useRef(requireFullscreen);
  const detectorRef     = useRef(null);
  const tfReadyRef      = useRef(false);
  const attemptIdRef    = useRef(attemptId);
  const wsRefLocal      = useRef(wsRef);
  // Persistent canvas for violation snapshots (allocated once)
  const snapCanvasRef   = useRef(null);

  // Counters — need enough consecutive bad frames before firing
  const noFaceCount     = useRef(0);
  const multiFaceCount  = useRef(0);
  const lookAwayCount   = useRef(0);
  const coveredCount    = useRef(0);
  // "face too small" disabled — MediaPipe returns tiny boxes for webcam close-ups
  const prevPixels      = useRef(null);
  const lastFired       = useRef({});

  enabledRef.current         = enabled;
  attemptIdRef.current       = attemptId;
  wsRefLocal.current         = wsRef;
  requireFsRef.current       = requireFullscreen;

  const captureSnapshotRef   = useRef(null);
  captureSnapshotRef.current = captureSnapshot;

  // FIX 2: initialise from server so warnings survive page refresh
  const [warnings,      setWarnings]      = useState(initialWarnings);
  const [trustScore,    setTrustScore]    = useState(100);
  const [events,        setEvents]        = useState([]);
  const [cameraReady,   setCameraReady]   = useState(false);
  const [proctorStatus, setProctorStatus] = useState('initializing');
  const [tfStatus,      setTfStatus]      = useState('loading');

  const setCameraReadyBoth = useCallback((val) => {
    cameraReadyRef.current = val;
    setCameraReady(val);
  }, []);

  // Cooldown — same event type won't fire more than once per N ms
  const canFire = (type, cooldownMs = 15000) => {
    const now = Date.now();
    if ((now - (lastFired.current[type] || 0)) < cooldownMs) return false;
    lastFired.current[type] = now;
    return true;
  };

  // Log event — reads from refs, no stale closures
  const logEventRef = useRef(null);
  logEventRef.current = async (eventType, severity, description, confidence = 0.9) => {
    if (!enabledRef.current) return;
    const event = { type: eventType, severity, description, time: new Date(), confidence };
    setEvents(prev => [...prev.slice(-60), event]);
    const deductions = { low: 3, medium: 10, high: 18, critical: 28 };
    const deduct = deductions[severity] || 10;

    // CRITICAL FIX: capture the snapshot exactly ONCE and reuse it for both the
    // WS live-preview and the evidence POST. Previously this ran TWICE per
    // violation (once for WS, once for axios) — and worse, the WS capture was
    // INSIDE a setTrustScore(prev => ...) updater function. React.StrictMode
    // (enabled in this app) deliberately invokes updater functions TWICE in
    // development to catch side-effect bugs like this, so the canvas encode +
    // WS send were firing up to 3x in a single tick. That synchronous CPU
    // spike (canvas.toDataURL is slow) made the tab briefly unresponsive at
    // the exact moment of every violation — which is what was kicking the
    // browser out of fullscreen ("minimizing") on every single violation.
    let snapshot = null;
    try { snapshot = captureSnapshotRef.current?.() || null; } catch { /* silent */ }

    // Pure state update — no side effects inside the updater function.
    let nextTrustScore = 100;
    setTrustScore(prev => {
      nextTrustScore = Math.max(0, prev - deduct);
      return nextTrustScore;
    });

    // Side effects run exactly once, outside the updater.
    try {
      wsRefLocal.current?.current?.send(JSON.stringify({
        type: 'violation', eventType, severity, description, confidence,
        trustScore: nextTrustScore, snapshot_url: snapshot,
      }));
    } catch { /* silent */ }

    // FIX 1: only count toward warning limit if the violation type is not disabled by org
    const isDisabled = disabledViolations.includes(eventType);
    if (!isDisabled) {
      setWarnings(prev => prev + 1);
    }
    setProctorStatus('warning');
    setTimeout(() => setProctorStatus(s => s === 'warning' ? 'active' : s), 3000);
    const aid = attemptIdRef.current;
    if (!aid) return;
    try {
      await axios.post(`/api/attempts/${aid}/proctor/event`, {
        event_type: eventType, severity, description,
        confidence_score: confidence,
        snapshot_data: snapshot, // reuse same capture — no second canvas encode
      });
    } catch { /* silent */ }
  };

  const logEvent = (eventType, severity, description, confidence) => {
    logEventRef.current?.(eventType, severity, description, confidence);
  };

  // Load TF via CDN
  const loadTF = useCallback(() => {
    setTfStatus('loading');
    const loadScript = (src) => new Promise((res, rej) => {
      if (document.querySelector(`script[src="${src}"]`)) { res(); return; }
      const s = document.createElement('script');
      s.src = src; s.async = true;
      s.onload = res; s.onerror = rej;
      document.head.appendChild(s);
    });
    loadScript('https://cdn.jsdelivr.net/npm/@tensorflow/tfjs@4.15.0/dist/tf.min.js')
      .then(() => loadScript('https://cdn.jsdelivr.net/npm/@tensorflow-models/face-detection@1.0.2/dist/face-detection.min.js'))
      .then(() => initDetector())
      .catch(() => setTfStatus('pixel-ai'));
  }, []);

  const initDetector = async () => {
    try {
      const fd = window.faceDetection;
      if (!fd) { setTfStatus('pixel-ai'); return; }
      await window.tf?.ready?.();
      const detector = await fd.createDetector(
        fd.SupportedModels.MediaPipeFaceDetector,
        { runtime: 'tfjs', maxFaces: 2, modelType: 'short' }  // maxFaces:2, short model = faster
      );
      detectorRef.current = detector;
      tfReadyRef.current  = true;
      setTfStatus('ready');
      console.log('[Proctor] TF face detector READY');
    } catch (err) {
      console.warn('[Proctor] Detector init failed:', err.message);
      setTfStatus('pixel-ai');
    }
  };

  const drawBoxes = (faces, W, H) => {
    const overlay = overlayRef.current;
    if (!overlay) return;
    overlay.width  = W;
    overlay.height = H;
    const ctx = overlay.getContext('2d');
    ctx.clearRect(0, 0, W, H);
    faces.forEach((face, i) => {
      const box = face.box || face.boundingBox;
      if (!box) return;
      const { xMin, yMin, width, height } = box;
      const color = i === 0 ? '#10b981' : '#ef4444';
      ctx.strokeStyle = color; ctx.lineWidth = 2.5;
      ctx.strokeRect(xMin, yMin, width, height);
      ctx.fillStyle = color; ctx.font = 'bold 11px monospace';
      const score = face.score ? `${Math.round(face.score*100)}%` : '';
      ctx.fillText(i === 0 ? `✓ Face 1 ${score}` : `⚠ Extra ${i+1}`, xMin+2, yMin - 5);
      face.keypoints?.slice(0,6).forEach(kp => {
        ctx.beginPath(); ctx.arc(kp.x, kp.y, 2, 0, 2*Math.PI);
        ctx.fillStyle = color; ctx.fill();
      });
    });
  };

  const runTFDetection = async () => {
    const video = videoRef.current;
    if (!video || !detectorRef.current || video.readyState < 2) return;
    const W = video.videoWidth || 640;
    const H = video.videoHeight || 480;
    let faces = [];
    try { faces = await detectorRef.current.estimateFaces(video); }
    catch { return; }
    drawBoxes(faces, W, H);
    const count = faces.length;

    // A. No face detected
    if (count === 0) {
      noFaceCount.current++;
      multiFaceCount.current = 0;
      // Require 5 consecutive frames with no face (15 seconds at 3s interval)
      if (noFaceCount.current >= 5 && canFire('face_not_detected', 20000)) {
        logEvent('face_not_detected', 'high', 'No face detected — student may have left the camera', 0.93);
        noFaceCount.current = 0;
      }
    } else {
      noFaceCount.current = 0;
    }

    // B. Multiple faces
    if (count > 1) {
      multiFaceCount.current++;
      if (multiFaceCount.current >= 3 && canFire('multiple_faces', 20000)) {
        logEvent('multiple_faces', 'critical', `${count} faces detected — another person is present`, 0.96);
        multiFaceCount.current = 0;
      }
    } else {
      multiFaceCount.current = 0;
    }

    if (count >= 1) {
      const primary = faces[0];
      const box = primary.box || primary.boundingBox;
      const score = primary.score ?? 1;

      // C. REMOVED "face too small" check — causes false positives with webcams
      // MediaPipe returns small bounding boxes for users sitting close to camera

      // D. Face confidence too low (genuinely obscured/covered face)
      // Only fire if confidence is VERY low (< 40%), not just moderate
      if (score < 0.40 && box) {
        const frac = (box.width * box.height) / (W * H);
        // Only flag if face area is also reasonable (rule out tracking artifacts)
        if (frac > 0.01 && canFire('face_obscured', 20000)) {
          logEvent('face_not_detected', 'medium', `Face appears obscured or covered (detection confidence: ${(score*100).toFixed(0)}%)`, 0.72);
        }
      }

      // E. Face significantly off-centre for many frames (genuinely looking away)
      if (box) {
        const cx = (box.xMin + box.width  / 2) / W;
        const cy = (box.yMin + box.height / 2) / H;
        // Looser threshold — allow more head movement
        const wayOffCenter = Math.abs(cx - 0.5) > 0.42 || cy < 0.05 || cy > 0.92;
        if (wayOffCenter) {
          lookAwayCount.current++;
          if (lookAwayCount.current >= 6 && canFire('looking_away', 20000)) {
            logEvent('looking_away', 'medium', 'Student is consistently not facing the camera', 0.82);
            lookAwayCount.current = 0;
          }
        } else {
          lookAwayCount.current = 0;
        }
      }
    }

    runBrightnessCheck();
  };

  const runPixelDetection = () => {
    const video  = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || video.readyState < 2) return;
    const W = video.videoWidth || 320;
    const H = video.videoHeight || 240;
    canvas.width = W; canvas.height = H;
    const ctx = canvas.getContext('2d');
    try { ctx.drawImage(video, 0, 0, W, H); } catch { return; }
    const { data: px } = ctx.getImageData(0, 0, W, H);

    let bSum = 0;
    for (let i = 0; i < px.length; i += 4)
      bSum += 0.299*px[i] + 0.587*px[i+1] + 0.114*px[i+2];
    const avgB = bSum / (W * H);

    if (avgB < 12) {
      coveredCount.current++;
      if (coveredCount.current >= 3 && canFire('face_not_detected', 18000)) {
        logEvent('face_not_detected', 'high', `Camera appears covered or blocked (brightness: ${avgB.toFixed(0)})`, 0.95);
        coveredCount.current = 0;
      }
      prevPixels.current = null;
      return;
    } else { coveredCount.current = 0; }

    // Motion detection
    if (prevPixels.current?.length === px.length) {
      let diff = 0;
      for (let i = 0; i < px.length; i += 48)
        diff += Math.abs(px[i]-prevPixels.current[i]) + Math.abs(px[i+1]-prevPixels.current[i+1]) + Math.abs(px[i+2]-prevPixels.current[i+2]);
      const motion = diff / (px.length/48*3);
      if (motion > 65 && canFire('suspicious_movement', 15000))
        logEvent('suspicious_movement', 'medium', `High motion detected (score: ${motion.toFixed(0)})`, 0.75);
    }
    prevPixels.current = new Uint8ClampedArray(px);

    // Skin-tone face detection
    let skin = 0;
    const x0=Math.floor(W*.1), x1=Math.floor(W*.9), y0=Math.floor(H*.05), y1=Math.floor(H*.95);
    for (let y=y0; y<y1; y+=3) {
      for (let x=x0; x<x1; x+=3) {
        const i=(y*W+x)*4;
        const r=px[i],g=px[i+1],b=px[i+2];
        const maxC=Math.max(r,g,b), minC=Math.min(r,g,b);
        if (r>60&&g>30&&b>15&&r>b&&r>=g&&(r-b)>15&&maxC-minC>10&&r<255&&g<240) skin++;
      }
    }
    const zone = ((x1-x0)/3)*((y1-y0)/3);
    const ratio = skin/zone;

    if (ratio < 0.04) {
      noFaceCount.current++;
      if (noFaceCount.current >= 5 && canFire('face_not_detected', 18000)) {
        logEvent('face_not_detected', 'high', `Face not visible in camera (skin ratio: ${(ratio*100).toFixed(1)}%)`, 0.82);
        noFaceCount.current = 0;
      }
    } else { noFaceCount.current = 0; }

    if (ratio > 0.40 && canFire('multiple_faces', 22000))
      logEvent('multiple_faces', 'critical', `Multiple people possibly detected (skin region: ${(ratio*100).toFixed(0)}%)`, 0.76);

    // Overlay debug info
    const overlay = overlayRef.current;
    if (overlay) {
      overlay.width=W; overlay.height=H;
      const oc = overlay.getContext('2d');
      oc.clearRect(0,0,W,H);
      const c = ratio>0.09?'#10b981':ratio>0.03?'#f59e0b':'#ef4444';
      oc.strokeStyle=c; oc.lineWidth=2; oc.strokeRect(4,4,W-8,H-8);
      oc.fillStyle=c; oc.font='bold 10px monospace';
      oc.fillText(`Skin:${(ratio*100).toFixed(0)}% Bright:${avgB.toFixed(0)}`, 8, H-6);
    }
  };

  const runBrightnessCheck = () => {
    const video=videoRef.current, canvas=canvasRef.current;
    if (!video||!canvas||video.readyState<2) return;
    const W=Math.min(video.videoWidth||160,160), H=Math.min(video.videoHeight||120,120);
    canvas.width=W; canvas.height=H;
    const ctx=canvas.getContext('2d');
    try { ctx.drawImage(video,0,0,W,H); } catch { return; }
    const { data: px } = ctx.getImageData(0,0,W,H);
    let bSum=0;
    for (let i=0;i<px.length;i+=4) bSum+=0.299*px[i]+0.587*px[i+1]+0.114*px[i+2];
    const avgB=bSum/(W*H);
    if (avgB<12) {
      coveredCount.current++;
      if (coveredCount.current>=3 && canFire('face_not_detected',18000)) {
        logEvent('face_not_detected','high',`Camera blocked — very dark frame (brightness:${avgB.toFixed(0)})`,0.96);
        coveredCount.current=0;
      }
    } else { coveredCount.current=0; }
  };

  const runAnalysis = async () => {
    if (!enabledRef.current || !cameraReadyRef.current) return;
    if (tfReadyRef.current && detectorRef.current) {
      await runTFDetection();
    } else {
      runPixelDetection();
    }
  };

  const startCamera = async () => {
    if (streamRef.current) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width:{ideal:640}, height:{ideal:480}, facingMode:'user' },
        audio: false,
      });
      streamRef.current = stream;
      const video = videoRef.current;
      if (video) {
        video.srcObject = stream;
        video.onloadedmetadata = () => {
          video.play().catch(()=>{});
          setCameraReadyBoth(true);
          setProctorStatus('active');
        };
      }
    } catch (err) {
      console.warn('[Proctor] Camera failed:', err.message);
      setCameraReadyBoth(false);
      setProctorStatus('active');
    }
  };

  const stopCamera = () => {
    streamRef.current?.getTracks().forEach(t=>t.stop());
    streamRef.current = null;
    setCameraReadyBoth(false);
    const o=overlayRef.current;
    if (o) o.getContext('2d')?.clearRect(0,0,o.width,o.height);
  };

  // Browser behaviour listeners — run when exam is ready, even without AI proctoring
  // Tab switch, fullscreen exit, window blur always fire if require_fullscreen is true
  useEffect(() => {
    // enabled = AI proctoring ON; requireFsRef = fullscreen required
    // Visibility/focus events fire when EITHER is active
    const shouldMonitorBehaviour = enabled || requireFsRef.current;
    if (!shouldMonitorBehaviour) return;

    const onVis = () => {
      if (document.hidden && canFire('tab_switch', 8000))
        logEvent('tab_switch', 'high', 'Student switched to another tab or minimized the browser');
    };
    const onBlur = () => {
      if (canFire('window_blur', 10000))
        logEvent('window_blur', 'high', 'Student left the exam window — possible tab switch or app switch');
    };
    const onCopy  = e => { e.preventDefault(); if (enabled && canFire('copy_paste',8000)) logEvent('copy_paste','medium','Copy attempt detected'); };
    const onPaste = e => { e.preventDefault(); if (enabled && canFire('copy_paste',8000)) logEvent('copy_paste','medium','Paste attempt detected'); };
    const onCtx   = e => { e.preventDefault(); if (enabled && canFire('right_click',12000)) logEvent('right_click','low','Right-click attempted'); };
    const onKey   = e => {
      const bad=[
        e.ctrlKey&&(e.key==='c'||e.key==='v'||e.key==='u'),
        e.metaKey&&(e.key==='c'||e.key==='v'),
        e.ctrlKey&&e.shiftKey&&(e.key==='I'||e.key==='J'),
        e.key==='F12', e.altKey&&e.key==='Tab', e.key==='PrintScreen',
      ];
      if (bad.some(Boolean)) {
        e.preventDefault();
        if (enabled && canFire('keyboard_shortcut',10000))
          logEvent('keyboard_shortcut','medium',`Suspicious shortcut: ${e.ctrlKey?'Ctrl+':''}${e.altKey?'Alt+':''}${e.key}`);
      }
    };
    const onFs = () => {
      if (!document.fullscreenElement && requireFsRef.current) {
        if (canFire('full_screen_exit', 5000))
          logEvent('full_screen_exit', 'high', 'Student exited fullscreen — exam requires fullscreen at all times');
        onFullscreenExit?.();
      }
    };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('blur', onBlur);
    document.addEventListener('copy', onCopy);
    document.addEventListener('paste', onPaste);
    document.addEventListener('contextmenu', onCtx);
    document.addEventListener('keydown', onKey);
    document.addEventListener('fullscreenchange', onFs);
    // Also detect window focus returning after being away
    const onFocus = () => {
      if (requireFsRef.current && !document.fullscreenElement && canFire('returned_no_fullscreen', 8000))
        logEvent('window_focus_no_fullscreen', 'high', 'Returned to exam but not in fullscreen — possible screen switch');
    };
    window.addEventListener('focus', onFocus);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('blur', onBlur);
      document.removeEventListener('copy', onCopy);
      document.removeEventListener('paste', onPaste);
      document.removeEventListener('contextmenu', onCtx);
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('fullscreenchange', onFs);
      window.removeEventListener('focus', onFocus);
    };
  }, [enabled]); // eslint-disable-line

  useEffect(() => {
    if (!enabled) return;
    enabledRef.current = true;
    startCamera();
    loadTF();
    intervalRef.current = setInterval(runAnalysis, 6000); // every 6s — less CPU
    return () => {
      enabledRef.current = false;
      clearInterval(intervalRef.current);
      stopCamera();
      prevPixels.current = null;
    };
  }, [enabled]);

  return { videoRef, canvasRef, overlayRef, warnings, trustScore, events, cameraReady, proctorStatus, tfStatus };
}
